import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { countries, states } from '../../Components/Enums/DataEnums';
import { toast } from 'sonner';
import { DatePicker } from '../../Components/Common/DatePicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faCheck } from '@fortawesome/free-solid-svg-icons';
import { saveInquiriesBulk } from './ThreadsAPI';
import { getUsersArr, isAgencyAllowedFutureDT } from '../../Store/Reducers';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../../Components/UI/button';
import { Card, CardContent } from '../../Components/UI/card';
import { setGlobalLoading } from '../../Store/Actions';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../Components/UI/tooltip';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '../../Components/UI/dialog';
import { Check, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Checkbox } from '../../Components/UI/checkbox';
import { Input } from '../../Components/UI/input';
import { Textarea } from '../../Components/UI/textarea';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../../Components/UI/select';
import { cn } from '../../Lib/Cn';
import { pluralize, validatorObj, normalizeDateFormat } from '../../Utils/Utilities';
import { getThreadNameFromTypeID, getTitleCase } from '../../Utils/Utilities';
import BusinessInput from '../../Components/Common/BusinessInput';
import MobileInput from '../../Components/Common/MobileInput';

// Common CSS classes for consistent styling
const threadImpClasses = {
  baseInput: "w-full text-sm bg-white text-gray-800 sahi-radius border focus:ring-0 px-3 py-2 h-10 transition-colors shadow-none outline-none",
  errorInput: "!border !border-red-300 !bg-red-50/40 text-gray-900 hover:!border-red-400 focus:!border-red-400 focus:!ring-0",
  activeErrorInput: "!border-2 !border-red-500 !bg-red-50/70 text-gray-900 hover:!border-red-500 focus:!border-red-500 focus:!ring-0 shadow-none",
  normalInput: "border-gray-200 hover:border-gray-300 focus:border-gray-400",
  errorHighlight: "",
  errorText: "text-red-500 text-xs font-normal whitespace-normal break-words mt-1 leading-tight",
  button: "button-config text-base flex items-center py-[6px] px-[10px]",
  primaryButton: "sahi-primary-button",
  saveButton: "!bg-sahi-green text-base text-white py-[6px] px-[10px] sahi-radius disabled:cursor-not-allowed disabled:opacity-55 hover:!bg-sahi-white hover:!text-sahi-green border border-sahi-green",
  checkbox: "h-4 w-4 data-[state=checked]:bg-sahi-primary-color data-[state=checked]:border-sahi-primary-color data-[state=checked]:text-white transition-colors",
  card: "bg-sahi-white flex-1 flex flex-col overflow-hidden sahi-radius !pt-0 border-none shadow-sm"
};

// Configuration constants for validation and business rules
const threadConst = {
  maxFieldLen: {
    bizName: 100, contactName: 100, designation: 100, email: 50,
    city: 50, state: 50, product: 200, quantity: 500,
    notes: 1000, source: 50, nextTalkNotes: 1000
  },
  mobValidation: {
    india: { length: 10 },
    international: { min: 7, max: 15 }
  },
  reqFields: ['BizName', 'ContactName', 'Mobile', 'Email'],
  typeID: 1, STAGE: 1, TYPE: 'INQ', CURR_STAGE: 'New',
  CHUNK_SIZE: 150,
  VALIDATION_BATCH_SIZE: 150
};

// Table cell component with memoization
const TableCell = React.memo(({ column, row, rowIndex, fieldIndex, hasError, rowHasError, currentError, errorMessage, renderInputField }) => {
  const isCurrentErrorCell = currentError.rowIndex === rowIndex && currentError.fieldIndex === fieldIndex;

  return (
    <td
      id={`cell-${rowIndex}-${fieldIndex}`}
      key={`${column.field}-${rowIndex}`}
      className="px-3 py-2.5 align-top text-sm text-text-color transition-colors"
      style={{ minWidth: column.maxWidth, maxWidth: column.maxWidth }}
    >
      {renderInputField(column, row, rowIndex, fieldIndex, isCurrentErrorCell, rowHasError)}
      {hasError && errorMessage && (
        <p className={threadImpClasses.errorText}>{errorMessage}</p>
      )}
    </td>
  );
}, (prevProps, nextProps) => {
  let cellValueChanged = prevProps.row[prevProps.column.field] !== nextProps.row[nextProps.column.field];

  // Special check: If this is the Mobile field, re-render if the Country Code (CC) changed
  if (prevProps.column.field === "Mobile" && prevProps.row.CC !== nextProps.row.CC) {
    cellValueChanged = true;
  }
  if (prevProps.column.field === "Model" && prevProps.row.Product !== nextProps.row.Product) {
    cellValueChanged = true;
  }

  // Special check: If this is the No column, re-render if the row error state changed
  if (prevProps.column.field === "No" && prevProps.rowHasError !== nextProps.rowHasError) {
    return false;
  }

  const errorStateChanged = prevProps.hasError !== nextProps.hasError || prevProps.errorMessage !== nextProps.errorMessage;
  const currentErrorChanged = (prevProps.currentError.rowIndex === prevProps.rowIndex && prevProps.currentError.fieldIndex === prevProps.fieldIndex) !==
    (nextProps.currentError.rowIndex === nextProps.rowIndex && nextProps.currentError.fieldIndex === nextProps.fieldIndex);

  return !cellValueChanged && !errorStateChanged && !currentErrorChanged;
});


// Table row component
const TableRow = React.memo(({ row, rowIndex, columns, errorCells, cellErrors, currentError, getCellKey, renderInputField }) => {
  const rowHasError = columns.some((_, fieldIndex) => errorCells.has(getCellKey(rowIndex, fieldIndex)));

  return (
    <tr id={`row-${rowIndex}`} className="hover:bg-gray-50/60 transition-colors">
      {columns.map((col, fieldIndex) => {
        const cellKey = getCellKey(rowIndex, fieldIndex);
        const hasError = errorCells.has(cellKey);
        const errorMessage = cellErrors.get(cellKey);

        return (
          <TableCell
            key={`${col.field}-${rowIndex}`}
            column={col}
            row={row}
            rowIndex={rowIndex}
            fieldIndex={fieldIndex}
            hasError={hasError}
            rowHasError={rowHasError}
            errorMessage={errorMessage}
            currentError={currentError}
            renderInputField={renderInputField}
          />
        );
      })}
    </tr>
  );
}, (prevProps, nextProps) => {
  const rowChanged = prevProps.row !== nextProps.row;
  const errorStateChanged = prevProps.errorCells !== nextProps.errorCells || prevProps.cellErrors !== nextProps.cellErrors;
  const currentErrorChanged = prevProps.currentError !== nextProps.currentError;

  return !rowChanged && !errorStateChanged && !currentErrorChanged;
});

// Virtualized table component for performance with large datasets
function VirtualizedTable({ data, columns, errorCells, cellErrors, currentError, getCellKey, renderInputField }) {
  const parentRef = useRef();
  const headerRef = useRef();
  const [containerHeight, setContainerHeight] = useState(0);
  const [debugInfo, setDebugInfo] = useState({});

  useEffect(() => {
    const updateHeight = () => {
      if (parentRef.current) {
        const height = parentRef.current.clientHeight;
        const scrollHeight = parentRef.current.scrollHeight;
        const scrollWidth = parentRef.current.scrollWidth;
        const clientWidth = parentRef.current.clientWidth;

        setContainerHeight(height);
        setDebugInfo(prev => ({
          ...prev,
          containerHeight: height,
          scrollHeight,
          canScroll: scrollHeight > height
        }));
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  // Sync horizontal scroll between header and data rows
  useEffect(() => {
    const handleScroll = (e) => {
      if (headerRef.current) {
        headerRef.current.scrollLeft = e.target.scrollLeft;
      }
    };

    const scrollContainer = parentRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const rowVirtualizer = useVirtualizer({
    count: data.length + 1,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => index === 0 ? 42 : 82,
    overscan: 10,
  });

  return (
    <div className="w-full h-full flex flex-col overflow-hidden bg-white">
      {/* Sticky Header with Horizontal Scroll Sync */}
      <div ref={headerRef} className="w-full bg-white border-b border-gray-200 z-20 sticky top-0 overflow-x-auto scrollbar-hide shadow-none">
        <table className="table-auto border-collapse w-full">
          <thead>
            <tr className="bg-white h-12">
              {columns.map((col, idx) => (
                <th
                  key={idx}
                  className="table-header-text px-3 py-3 text-left font-semibold text-sm text-text-color bg-white select-none whitespace-nowrap"
                  style={{ minWidth: col.maxWidth, maxWidth: col.maxWidth }}
                >
                  {col.display}
                </th>
              ))}
            </tr>
          </thead>
        </table>
      </div>

      {/* Scrollable Data Rows */}
      <div
        ref={parentRef}
        className="flex-1 w-full overflow-y-scroll overflow-x-auto pt-2"
        style={{ minHeight: 0 }}
      >
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize() - 42}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {(() => {
            const virtualItems = rowVirtualizer.getVirtualItems();
            return virtualItems.filter(item => item.index > 0).map((virtualItem) => (
              <div
                key={virtualItem.key}
                className='w-full'
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualItem.size}px`,
                  transform: `translateY(${virtualItem.start - 42}px)`,
                }}
              >
                <table className="table-auto border-collapse w-full">
                  <tbody>
                    <TableRow
                      row={data[virtualItem.index - 1]}
                      rowIndex={virtualItem.index - 1}
                      columns={columns}
                      errorCells={errorCells}
                      cellErrors={cellErrors}
                      currentError={currentError}
                      getCellKey={getCellKey}
                      renderInputField={renderInputField}
                    />
                  </tbody>
                </table>
              </div>
            ));
          })()}
        </div>
      </div>
    </div>
  );
}

// Custom hook for cell validation logic
const useThreadValidation = (users, inquiryData, products = [], customFields = []) => {
  const allowFutureDT = useSelector(isAgencyAllowedFutureDT);

  const getMonthEndingDate = (referenceDate = null) => {
    const date = referenceDate ? new Date(referenceDate) : new Date();
    const year = date.getFullYear();
    const month = date.getMonth();
    const lastDay = new Date(year, month + 1, 0);
    return lastDay.toISOString().split('T')[0];
  };

  // Validates individual cell based on field type and business rules
  const validateSingleCell = useCallback((data, rowIndex, fieldIndex, columns, getCellKey) => {
    const row = data[rowIndex];
    const field = columns[fieldIndex].field;
    let errorMessage = null;

    const validationRules = {
      BizName: () => {
        const biz = row.BizName?.trim();
        const contact = row.ContactName?.trim();

        if (!biz && !contact) return 'Either company or contact name is required';

        if (biz && biz.length > threadConst.maxFieldLen.bizName)
          return 'Company name is too long';

        return null;
      },

      ContactName: () => {
        const biz = row.BizName?.trim();
        const contact = row.ContactName?.trim();

        if (!contact && !biz) return 'Either company or contact name is required';

        if (contact && contact.length > threadConst.maxFieldLen.contactName)
          return 'Contact name is too long';

        return null;
      },

      Designation: () => row.Designation && row.Designation.length > threadConst.maxFieldLen.designation ? 'Designation is too long' : null,

      Mobile: () => {
        const mobVal = String(row.Mobile || '').trim();
        if (!mobVal) return 'Mobile number is required';

        // If someone pastes with '+' here, the onChange handles it, 
        // but we add a safety check for the validation string itself
        const digitsOnly = mobVal.replace(/\D/g, '');
        if (!/^\d+$/.test(digitsOnly)) return 'Mobile must contain digits only';
        if (/^0\d+/.test(row.Mobile)) return 'Mobile cannot start with 0';
        if (!validatorObj.validateMobile(row.Mobile, row.CountryID)) {
          return row.CountryID === 1 ? 'Mobile must be exactly 10 digits for India' : 'Mobile must be 7 to 15 digits';
        }
        if (/\s/.test(row.Mobile)) return 'Mobile cannot contain spaces';

        const rowCC = String(row.CC || '91');
        if (inquiryData?.some(inq => inq.Mobile === row.Mobile && String(inq.CC || '91') === rowCC)) {
          return 'Mobile number already exists in your inquiries';
        }
        if (data.some((otherRow, otherIndex) => otherIndex !== rowIndex && otherRow.Mobile === row.Mobile && String(otherRow.CC || '91') === rowCC)) {
          return 'Duplicate mobile number';
        }
        return null;
      },

      Email: () => {
        if (!row.Email?.trim()) return null;
        if (/\s/.test(row.Email)) return 'Email cannot contain spaces';
        if (row.Email.length > threadConst.maxFieldLen.email) return 'Email is too long';
        if (!validatorObj.validateEmail(row.Email)) return 'Invalid email format';

        if (inquiryData?.some(inq => inq.Email === row.Email)) return 'Email already exists';
        if (data.some((otherRow, otherIndex) => otherIndex !== rowIndex && otherRow.Email === row.Email)) {
          return 'Duplicate email';
        }
        return null;
      },

      Country: () => row.Country?.trim() && !countries.find(c => c.Country.toLowerCase() === row.Country.toLowerCase()) ? 'Invalid country' : null,

      State: () => {
        if (!row.State?.trim()) return null;
        if (row.State.length > threadConst.maxFieldLen.state) return 'State name is too long';
        if (row.CountryID === 1 && !states.find(s => s.State.toLowerCase() === row.State.toLowerCase() && s.CountryID === 1)) {
          return 'Invalid Indian state';
        }
        return null;
      },

      City: () => row.City?.trim() && row.City.length > threadConst.maxFieldLen.city ? 'City name is too long' : null,
      Source: () => row.Source?.trim() && row.Source.length > threadConst.maxFieldLen.source ? 'Source name is too long' : null,
      Product: () => {
        if (!row.Product?.trim()) return null;
        if (row.Product.length > threadConst.maxFieldLen.product) return 'Product description is too long';

        const matchingProduct = products.find(p =>
          (p.name || p.PNm || '').toLowerCase() === row.Product.toLowerCase()
        );
        return !matchingProduct ? 'Please select a valid product from the options' : null;
      },

      Model: () => {
        return null;
      },
      TargetDT: () => {
        if (!row.TargetDT?.trim()) return 'Target date is required for orders';
        return validatorObj.validateDate(row.TargetDT, { allowPast: false, isRequired: true });
      },

      Amount: () => {
        if (!row.Amount) return 'Amount is required for orders';
        const amount = parseFloat(row.Amount);
        if (isNaN(amount) || amount < 0) return 'Amount must be a valid positive number';
        return null;
      },
      BizProspect: () => {
        if (row.BizProspect === null || row.BizProspect === undefined || row.BizProspect === '') return 'Business Potential is required';
        const bizProspect = parseFloat(row.BizProspect);
        if (isNaN(bizProspect) || bizProspect < 0) return 'Business Potential must be a valid positive number';
        return null;
      },
      DueDT: () => {
        if (!row.DueDT?.trim()) return null;
        if (row.SinceDT?.trim()) {
          const sinceDateVal = new Date(row.SinceDT);
          const dueDateVal = new Date(row.DueDT);
          if (isNaN(sinceDateVal.getTime()) || isNaN(dueDateVal.getTime())) return 'Invalid date format';
          if (dueDateVal < sinceDateVal) return `Due Date cannot be before ${row.SinceDT}`;
          return null;
        }
        const dateValidation = validatorObj.validateDate(row.DueDT, { allowPast: false, isRequired: false });
        return dateValidation;
      },
      CompleteOnDT: () => {
        if (!row.CompleteOnDT?.trim()) return null;
        if (row.SinceDT?.trim()) {
          const sinceDateVal = new Date(row.SinceDT);
          const completeOnVal = new Date(row.CompleteOnDT);
          if (isNaN(sinceDateVal.getTime()) || isNaN(completeOnVal.getTime())) return 'Invalid date format';
          if (completeOnVal < sinceDateVal) return `Complete On cannot be before ${row.SinceDT}`;
          return null;
        }
        const dateValidation = validatorObj.validateDate(row.CompleteOnDT, { allowPast: false, isRequired: false });
        return dateValidation;
      },
      ExpectedByDT: () => {
        if (!row.ExpectedByDT?.trim()) return null;
        if (row.SinceDT?.trim()) {
          const sinceDateVal = new Date(row.SinceDT);
          const expectedByVal = new Date(row.ExpectedByDT);
          if (isNaN(sinceDateVal.getTime()) || isNaN(expectedByVal.getTime())) return 'Invalid date format';
          if (expectedByVal < sinceDateVal) return `Expected By cannot be before ${row.SinceDT}`;
          return null;
        }
        const dateValidation = validatorObj.validateDate(row.ExpectedByDT, { allowPast: false, isRequired: false });
        return dateValidation;
      },
      Notes: () => row.Notes?.trim() && row.Notes.length > threadConst.maxFieldLen.notes ? 'Notes are too long' : null,
      POCUserID: () => {
        if (!row.POCUserName?.trim()) return null;
        const user = users.find(u => u.name?.toLowerCase().trim() === row.POCUserName.toLowerCase().trim());
        return !user ? 'Please select a valid executive' : null;
      },
      SinceDT: () => validatorObj.validateDate(row.SinceDT, { allowFuture: allowFutureDT, isRequired: false }),
      NextTalkDT: () => {
        if (!row.NextTalkDT?.trim()) return null;

        const nextTalkDateVal = new Date(row.NextTalkDT);
        if (isNaN(nextTalkDateVal.getTime())) return 'Invalid date format';

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        nextTalkDateVal.setHours(0, 0, 0, 0);

        if (nextTalkDateVal < today) return 'Next Talk date cannot be in the past';

        // Only require notes if date is set and we're validating the date field specifically
        if (row.NextTalkDT?.trim() && !row.NextTalkNotes?.trim()) {
          return 'Next talk notes are required when date is set';
        }
        return null;
      },
      NextTalkNotes: () => {
        if (row.NextTalkNotes?.trim() && row.NextTalkNotes.length > threadConst.maxFieldLen.nextTalkNotes) {
          return 'Next talk notes are too long';
        }
        // Only require date if notes are set and we're validating the notes field specifically  
        if (row.NextTalkNotes?.trim() && !row.NextTalkDT?.trim()) {
          return 'Next talk date is required when notes are set';
        }
        return null;
      },
      // Custom fields validation
      ...customFields.reduce((acc, customField) => {
        acc[`CF_${customField.FID}`] = () => {
          const value = row[`CF_${customField.FID}`];
          return value && value.length > 50 ? `${customField.F} is too long (max 50 characters)` : null;
        };
        return acc;
      }, {})
    };

    if (validationRules[field]) {
      errorMessage = validationRules[field]();
    }

    return { cellKey: getCellKey(rowIndex, fieldIndex), errorMessage };
  }, [users, inquiryData, products, customFields, allowFutureDT]);

  return { validateSingleCell, getMonthEndingDate };
};

// Custom hook for managing cell focus and navigation
const useFocusManagement = (editableData, columns) => {
  const [currentFocusedCell, setCurrentFocusedCell] = useState({ rowIndex: -1, fieldIndex: -1 });
  const [previousCell, setPreviousCell] = useState({ rowIndex: -1, fieldIndex: -1 });

  // Calculates next cell position for tab navigation
  const getNextCell = useCallback((currentRowIndex, currentFieldIndex, direction = 'forward') => {
    const totalRows = editableData.length;
    const totalFields = columns.length;
    let nextRowIndex = currentRowIndex;
    let nextFieldIndex = currentFieldIndex;

    if (direction === 'forward') {
      nextFieldIndex++;
      if (nextFieldIndex >= totalFields) {
        nextFieldIndex = 1;
        nextRowIndex++;
        if (nextRowIndex >= totalRows) {
          nextRowIndex = 0;
        }
      }
    } else {
      nextFieldIndex--;
      if (nextFieldIndex < 1) {
        nextFieldIndex = totalFields - 1;
        nextRowIndex--;
        if (nextRowIndex < 0) {
          nextRowIndex = totalRows - 1;
        }
      }
    }

    if (nextFieldIndex === 0) {
      return getNextCell(nextRowIndex, nextFieldIndex, direction);
    }

    return { rowIndex: nextRowIndex, fieldIndex: nextFieldIndex };
  }, [editableData.length, columns.length]);

  return { currentFocusedCell, setCurrentFocusedCell, previousCell, setPreviousCell, getNextCell };
};

// Main component
const CorrectExcelThreads = React.memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { excelData: initialExcelData = [], inquiryData = [] } = location.state || {};

  // State management
  const [editableData, setEditableData] = useState([]);
  const [errorCells, setErrorCells] = useState(new Set());
  const [cellErrors, setCellErrors] = useState(new Map());
  const [currentError, setCurrentError] = useState({ rowIndex: -1, fieldIndex: -1 });
  const [saveDialog, setSaveDialog] = useState(false);
  const [ignoreErrors, setIgnoreErrors] = useState(false);
  const [apiError, setAPIError] = useState(null);
  const [validationProgress, setValidationProgress] = useState(0);
  const [isValidating, setIsValidating] = useState(false);
  const [currentDisplayPage, setCurrentDisplayPage] = useState(0);
  const validationAbortRef = useRef(false);

  const users = useSelector(getUsersArr);
  const reduxProducts = useSelector(state => state.masterData.products || [])
  const products = useMemo(() => reduxProducts.filter(prod => prod.IsD === false || prod.IsD === 0), [reduxProducts]);
  const threadTypes = useSelector(state => state.masterData.threadTypes || []);
  const taskTypes = useSelector(state => state.masterData.recordTypes || []);
  const thrExtraFields = useSelector(state => state.masterData.thrExtraFields || []);
  const allowFutureDT = useSelector(isAgencyAllowedFutureDT);
  const inputRefs = useRef([]);

  // Get custom fields for current thread type
  const customFields = useMemo(() => {
    const typeID = location.state?.threadInfo?.typeID || location.state?.threadType?.typeID || 1;
    const threadTypeFields = thrExtraFields.find(item => item.TID === typeID);

    if (threadTypeFields && threadTypeFields.Fields) {
      try {
        const parsedFields = JSON.parse(threadTypeFields.Fields);
        return parsedFields.filter(field => field.IsD === false);
      } catch (e) {
        console.error('Error parsing custom fields:', e);
        return [];
      }
    }
    return [];
  }, [thrExtraFields, location.state]);

  const { validateSingleCell, getMonthEndingDate } = useThreadValidation(users, inquiryData, products, customFields);

  const memoizedCountries = useMemo(() => countries, []);
  const memoizedStates = useMemo(() => states, []);

  const columns = useMemo(() => {
    const isOrder = (location.state?.threadInfo?.typeID || location.state?.threadType?.typeID) === 2;

    // Define custom field columns
    const customFieldColumns = customFields.map(field => ({
      field: `CF_${field.FID}`,
      maxWidth: '180px',
      type: 'text',
      display: field.F,
      customField: true,
      FID: field.FID
    }))

    if (isOrder) {
      // columns: company, contact name, mobile, email, city, state, country, order since, product, model, amount, business potential, target date, due date, complete on date, source, in-charge, task type, notes
      return [
        { field: 'No', maxWidth: '50px', type: 'text', display: 'No' },
        { field: 'BizName', maxWidth: '180px', type: 'text', display: <>Company</> },
        { field: 'ContactName', maxWidth: '180px', type: 'text', display: <>Contact Name</> },
        { field: 'Mobile', maxWidth: '200px', type: 'mobile', display: <>Mobile </> },
        { field: 'Email', maxWidth: '200px', type: 'email', display: <>Email </> },
        { field: 'City', maxWidth: '150px', type: 'text', display: 'City' },
        { field: 'State', maxWidth: '150px', type: 'dynamic', display: 'State' },
        { field: 'Country', maxWidth: '170px', type: 'select', options: countries.map(c => c.Country), display: 'Country' },
        { field: 'SinceDT', maxWidth: '180px', type: 'date', display: 'Sale Since' },
        { field: 'Product', maxWidth: '200px', type: 'productCombobox', display: 'Product' },
        { field: 'Model', maxWidth: '180px', type: 'modelCombobox', display: 'Model/Package' },
        { field: 'Amount', maxWidth: '180px', type: 'number', display: <>Amount </> },
        { field: 'BizProspect', maxWidth: '180px', type: 'number', display: 'Business Potential' },
        { field: 'TargetDT', maxWidth: '180px', type: 'date', display: <>Target Date <span className="text-red-500">*</span></> },
        { field: 'DueDT', maxWidth: '180px', type: 'date', display: 'Due Date' },
        { field: 'Source', maxWidth: '150px', type: 'text', display: 'Source' },
        { field: 'POCUserID', maxWidth: '150px', type: 'select', options: users.map(u => u.name), display: 'In-charge' },
        { field: 'NextTaskType', maxWidth: '200px', type: 'select', options: taskTypes.map(t => t.Typ), display: 'Next Task Type' },
        { field: 'Notes', maxWidth: '200px', type: 'textarea', display: 'Notes' },
        ...customFieldColumns,
      ];
    } else {
      // Inquiry columns: keep original structure with business potential, due date, expected by, and next talk fields
      return [
        { field: 'No', maxWidth: '50px', type: 'text', display: 'No' },
        { field: 'BizName', maxWidth: '180px', type: 'text', display: <>Company</> },
        { field: 'ContactName', maxWidth: '180px', type: 'text', display: <>Contact Name</> },
        { field: 'Designation', maxWidth: '150px', type: 'text', display: 'Designation' },
        { field: 'Mobile', maxWidth: '200px', type: 'mobile', display: <>Mobile </> },
        { field: 'Email', maxWidth: '200px', type: 'email', display: <>Email </> },
        { field: 'Country', maxWidth: '170px', type: 'select', options: countries.map(c => c.Country), display: 'Country' },
        { field: 'State', maxWidth: '150px', type: 'dynamic', display: 'State' },
        { field: 'City', maxWidth: '150px', type: 'text', display: 'City' },
        { field: 'Source', maxWidth: '150px', type: 'text', display: 'Source' },
        { field: 'POCUserID', maxWidth: '150px', type: 'select', options: users.map(u => u.name), display: 'In-charge' },
        { field: 'Product', maxWidth: '200px', type: 'productCombobox', display: 'Product' },
        { field: 'Model', maxWidth: '180px', type: 'modelCombobox', display: 'Model/Package' },
        { field: 'BizProspect', maxWidth: '180px', type: 'number', display: 'Business Potential' },
        { field: 'DueDT', maxWidth: '180px', type: 'date', display: 'Expected By' },
        { field: 'Quantity', maxWidth: '200px', type: 'textarea', display: 'Quantity' },
        { field: 'Notes', maxWidth: '200px', type: 'textarea', display: 'Notes' },
        { field: 'SinceDT', maxWidth: '180px', type: 'date', display: 'Lead Since' },
        { field: 'NextTalkDT', maxWidth: '180px', type: 'date', display: 'Next Talk on' },
        { field: 'NextTalkNotes', maxWidth: '200px', type: 'textarea', display: 'Next Talk Notes' },
        { field: 'NextTaskType', maxWidth: '200px', type: 'select', options: taskTypes.map(t => t.TypeName || t.name), display: 'Next Task Type' },
        ...customFieldColumns
      ];
    }
  }, [users, taskTypes, location.state?.threadInfo?.typeID, location.state?.threadType?.typeID, customFields]);

  const { currentFocusedCell, setCurrentFocusedCell, previousCell, setPreviousCell, getNextCell } = useFocusManagement(editableData, columns);

  // Utility functions
  const getCellKey = useCallback((rowIndex, fieldIndex) => `${rowIndex},${fieldIndex}`, []);

  // Gets dependent cells that need revalidation when current cell changes
  const getDependentCells = useCallback((rowIndex, fieldIndex, field) => {
    const dependents = [];
    const dependencyMap = {
      Country: 'State',
      NextTalkDT: 'NextTalkNotes',
      NextTalkNotes: 'NextTalkDT',
      Product: 'Model'
    }; // NextTalkDT/NextTalkNotes are internal field names; displayed as "Next Task on"/"Next Task Notes"

    if (!dependencyMap[field]) {
      return dependents;
    }

    const depFieldIndex = columns.findIndex(col => col.field === dependencyMap[field]);
    if (depFieldIndex !== -1) {
      dependents.push({ rowIndex, fieldIndex: depFieldIndex });
    }

    return dependents;
  }, [columns]);

  const validateAndClearInvalidModels = useCallback((data) => {
    return data.map(row => {
      if (!row.Product?.trim() || !row.Model?.trim()) {
        return row;
      }

      const matchingProduct = products.find(p =>
        (p.name || p.PNm || '').toLowerCase() === row.Product.toLowerCase()
      );

      if (!matchingProduct) {
        return row;
      }

      try {
        const modelsData = matchingProduct.Mdls || matchingProduct.mdls || matchingProduct.Models || matchingProduct.models;
        if (!modelsData) {
          return row;
        }

        const models = typeof modelsData === 'string' ? JSON.parse(modelsData) : modelsData;
        if (!Array.isArray(models) || models.length === 0) {
          return row;
        }

        const validModel = models.find(m =>
          (m.MNm || m.mNm || m.name || '').toLowerCase() === row.Model.toLowerCase() || m.MNm === 'Other' || m.mNm === 'Other'
        );

        if (!validModel) {
          return { ...row, Model: '' };
        }

        return row;
      } catch (e) {
        console.error('Error validating model:', e);
        return row;
      }
    });
  }, [products]);

  useEffect(() => {
    const initializeData = async () => {
      if (editableData.length > 0) return;

      dispatch(setGlobalLoading(true));
      validationAbortRef.current = false;
      setIsValidating(true);
      setValidationProgress(0);

      try {
        if (initialExcelData?.length > 0) {
          // Pre-process product matching and normalize dates
          const sanitizedData = initialExcelData.map(row => {
            const newRow = { ...row };
            if (newRow.TargetDT) {
              newRow.TargetDT = normalizeDateFormat(newRow.TargetDT);
            }
            if (newRow.SinceDT) {
              newRow.SinceDT = normalizeDateFormat(newRow.SinceDT);
            }
            if (newRow.NextTalkDT) {
              newRow.NextTalkDT = normalizeDateFormat(newRow.NextTalkDT);
            }
            if (newRow.DueDT) {
              newRow.DueDT = normalizeDateFormat(newRow.DueDT);
            }
            if (newRow.CompleteOnDT) {
              newRow.CompleteOnDT = normalizeDateFormat(newRow.CompleteOnDT);
            }
            if (newRow.ExpectedByDT) {
              newRow.ExpectedByDT = normalizeDateFormat(newRow.ExpectedByDT);
            }

            // Handle product matching
            if (newRow.Product) {
              const trimmedProduct = newRow.Product.trim().toLowerCase();
              const matchingProduct = products.find(p =>
                (p.name || p.PNm || '').toLowerCase().trim() === trimmedProduct
              );
              if (matchingProduct) {
                newRow.Product = matchingProduct.name || matchingProduct.PNm;

                if (newRow.Model) {
                  const trimmedModel = newRow.Model.trim().toLowerCase();
                  try {
                    const modelsData = matchingProduct.Mdls || matchingProduct.mdls || matchingProduct.Models || matchingProduct.models;
                    if (modelsData) {
                      const models = typeof modelsData === 'string' ? JSON.parse(modelsData) : modelsData;
                      if (Array.isArray(models)) {
                        const matchingModel = models.find(m =>
                          (m.MNm || m.mNm || m.name || '').toLowerCase().trim() === trimmedModel || trimmedModel === 'other'
                        );
                        if (matchingModel) {
                          newRow.Model = matchingModel.MNm || matchingModel.mNm || matchingModel.name;
                        } else {
                          newRow.Model = '';
                        }
                      }
                    }
                  } catch (e) {
                    console.error('Error matching model:', e);
                    newRow.Model = '';
                  }
                }
              } else {
                newRow.Product = '';
                newRow.Model = '';
              }
            } else {
              newRow.Product = '';
              newRow.Model = '';
            }

            // Handle In-charge matching
            if (newRow.POCUserName) {
              const matchingUser = users.find(u => u.name?.toLowerCase().trim() === newRow.POCUserName.trim().toLowerCase());
              newRow.POCUserID = matchingUser ? String(matchingUser.userID) : '';
              newRow.POCUserName = matchingUser ? matchingUser.name : '';
            } else {
              newRow.POCUserID = '';
              newRow.POCUserName = '';
            }

            if (!newRow.NextTaskType && newRow.NextTalkDT?.trim() && newRow.NextTalkNotes?.trim()) {
              newRow.NextTaskType = "Internal";
            }

            // Parse and map custom fields
            if (newRow.CustomFields || newRow.ExtraFields) {
              try {
                const customFieldsData = JSON.parse(newRow.CustomFields || newRow.ExtraFields);
                customFieldsData.forEach(cf => {
                  newRow[`CF_${cf.FID}`] = cf.V;
                });
              } catch (e) {
                console.error('Error parsing custom fields:', e);
              }
            }

            // Ensure default values for country/CC like in contact import
            newRow.CountryID = newRow.CountryID || 1;
            newRow.CC = newRow.CC || 91;
            newRow.Country = newRow.Country || (newRow.CountryID === 1 ? 'India' : '');

            return newRow;
          });

          setEditableData(sanitizedData);

          // Initialize refs only once with proper structure
          if (!inputRefs.current || inputRefs.current.length === 0) {
            inputRefs.current = sanitizedData.map(() =>
              columns.map(() => React.createRef())
            );
          }

          await new Promise((resolve) => {
            setTimeout(() => {
              validateAllData(sanitizedData);
              resolve();
            }, 0);
          });
        }
      } catch (error) {
        console.error('Error initializing data:', error);
        setAPIError('Failed to initialize data');
      } finally {
        setIsValidating(false);
        dispatch(setGlobalLoading(false));
      }
    };

    initializeData();
  }, [initialExcelData, products]);

  // Validate data in chunks to prevent UI blocking
  const validateDataInChunks = useCallback(async (data) => {
    const batchSize = threadConst.VALIDATION_BATCH_SIZE;
    let allErrorCells = new Set();
    let allCellErrors = new Map();

    for (let i = 0; i < data.length; i += batchSize) {
      if (validationAbortRef.current) break;

      const endIdx = Math.min(i + batchSize, data.length);

      // Validate this batch
      for (let rowIndex = i; rowIndex < endIdx; rowIndex++) {
        if (validationAbortRef.current) break;

        columns.forEach((col, fieldIndex) => {
          if (col.field !== 'No') {
            const { cellKey, errorMessage } = validateSingleCell(data, rowIndex, fieldIndex, columns, getCellKey);
            if (errorMessage) {
              allErrorCells.add(cellKey);
              allCellErrors.set(cellKey, errorMessage);
            }
          }
        });
      }

      // Check for duplicate mobiles/emails in current batch
      const mobileFieldIndex = columns.findIndex(col => col.field === 'Mobile');
      const emailFieldIndex = columns.findIndex(col => col.field === 'Email');

      if (mobileFieldIndex !== -1) {
        const valueToRows = new Map();
        for (let idx = i; idx < endIdx; idx++) {
          const value = data[idx].Mobile?.trim();
          if (value) {
            if (!valueToRows.has(value)) {
              valueToRows.set(value, []);
            }
            valueToRows.get(value).push(idx);
          }
        }

        valueToRows.forEach((rowIndices) => {
          if (rowIndices.length > 1) {
            rowIndices.forEach(rowIdx => {
              const cellKey = getCellKey(rowIdx, mobileFieldIndex);
              allErrorCells.add(cellKey);
              allCellErrors.set(cellKey, 'Duplicate mobile number');
            });
          }
        });
      }

      if (emailFieldIndex !== -1) {
        const valueToRows = new Map();
        for (let idx = i; idx < endIdx; idx++) {
          const value = data[idx].Email?.trim();
          if (value) {
            if (!valueToRows.has(value)) {
              valueToRows.set(value, []);
            }
            valueToRows.get(value).push(idx);
          }
        }

        valueToRows.forEach((rowIndices) => {
          if (rowIndices.length > 1) {
            rowIndices.forEach(rowIdx => {
              const cellKey = getCellKey(rowIdx, emailFieldIndex);
              allErrorCells.add(cellKey);
              allCellErrors.set(cellKey, 'Duplicate email');
            });
          }
        });
      }

      // Update progress
      const progress = 50 + Math.round((endIdx / data.length) * 50);
      setValidationProgress(progress);

      // Yield to main thread
      await new Promise(resolve => setTimeout(resolve, 0));
    }

    // Set final error states
    setErrorCells(allErrorCells);
    setCellErrors(allCellErrors);

    // Set current error
    if (allErrorCells.size > 0) {
      const firstError = Array.from(allErrorCells)[0];
      const [rowIndex, fieldIndex] = firstError.split(',').map(Number);
      setCurrentError({ rowIndex, fieldIndex });
    } else {
      setCurrentError({ rowIndex: -1, fieldIndex: -1 });
    }

    setValidationProgress(100);
  }, [columns, validateSingleCell, getCellKey]);

  // Validates data and updates error states
  const validateData = useCallback((data, cellCoordinate = null) => {
    let newErrorCells = new Set();
    let newCellErrors = new Map();

    if (cellCoordinate === null) {
      // Full validation - clear everything first
      data.forEach((row, rowIndex) => {
        columns.forEach((col, fieldIndex) => {
          if (col.field !== 'No') {
            const { cellKey, errorMessage } = validateSingleCell(data, rowIndex, fieldIndex, columns, getCellKey);
            if (errorMessage) {
              newErrorCells.add(cellKey);
              newCellErrors.set(cellKey, errorMessage);
            }
          }
        });
      });

      setErrorCells(newErrorCells);
      setCellErrors(newCellErrors);

      const errorCellsArray = Array.from(newErrorCells);
      if (errorCellsArray.length > 0 &&
        (currentError.rowIndex === -1 || !newErrorCells.has(getCellKey(currentError.rowIndex, currentError.fieldIndex)))) {
        const firstErrorCell = errorCellsArray[0];
        const [rowIndex, fieldIndex] = firstErrorCell.split(',').map(Number);
        setCurrentError({ rowIndex, fieldIndex });
      } else if (errorCellsArray.length === 0) {
        setCurrentError({ rowIndex: -1, fieldIndex: -1 });
      }
    } else {
      // Single cell validation - update atomically based on current state
      const { rowIndex, fieldIndex } = cellCoordinate;
      const field = columns[fieldIndex]?.field;

      setErrorCells(prevErrorCells => {
        const updatedErrorCells = new Set(prevErrorCells);

        setCellErrors(prevCellErrors => {
          const updatedCellErrors = new Map(prevCellErrors);

          const { cellKey, errorMessage } = validateSingleCell(data, rowIndex, fieldIndex, columns, getCellKey);

          if (errorMessage) {
            updatedErrorCells.add(cellKey);
            updatedCellErrors.set(cellKey, errorMessage);
          } else {
            updatedErrorCells.delete(cellKey);
            updatedCellErrors.delete(cellKey);
          }

          const dependentCells = getDependentCells(rowIndex, fieldIndex, field);
          dependentCells.forEach(({ rowIndex: depRowIndex, fieldIndex: depFieldIndex }) => {
            const { cellKey: depCellKey, errorMessage: depErrorMessage } =
              validateSingleCell(data, depRowIndex, depFieldIndex, columns, getCellKey);

            if (depErrorMessage) {
              updatedErrorCells.add(depCellKey);
              updatedCellErrors.set(depCellKey, depErrorMessage);
            } else {
              updatedErrorCells.delete(depCellKey);
              updatedCellErrors.delete(depCellKey);
            }
          });

          if (field === 'Mobile' || field === 'Email') {
            const duplicateMessage = field === 'Mobile' ? 'Duplicate mobile number' : 'Duplicate email';

            for (const key of Array.from(updatedErrorCells)) {
              if (updatedCellErrors.get(key) === duplicateMessage) {
                updatedErrorCells.delete(key);
                updatedCellErrors.delete(key);
              }
            }

            const targetFieldIndex = columns.findIndex(col => col.field === field);
            const valueToRows = new Map();

            data.forEach((row, rowIDX) => {
              const rawVal = row[field]?.trim();
              if (rawVal) {
                const uniqueKey = field === 'Mobile' ? `${row.CC || '91'}_${rawVal}` : rawVal;
                if (!valueToRows.has(uniqueKey)) {
                  valueToRows.set(uniqueKey, []);
                }
                valueToRows.get(uniqueKey).push(rowIDX);
              }
            });

            valueToRows.forEach((rowIndices) => {
              if (rowIndices.length > 1) {
                rowIndices.forEach(rowIDX => {
                  const key = getCellKey(rowIDX, targetFieldIndex);
                  updatedErrorCells.add(key);
                  updatedCellErrors.set(key, duplicateMessage);
                });
              }
            });
          }

          return updatedCellErrors;
        });

        // If the current error was on this cell or any cleared cell and is now valid, reset currentError
        setCurrentError(prev => {
          if (prev.rowIndex !== -1 && prev.fieldIndex !== -1) {
            const activeKey = getCellKey(prev.rowIndex, prev.fieldIndex);
            if (!updatedErrorCells.has(activeKey)) {
              return { rowIndex: -1, fieldIndex: -1 };
            }
          }
          return prev;
        });

        return updatedErrorCells;
      });
    }
  }, [currentError, columns, validateSingleCell, getCellKey, getDependentCells]);
  const validateAllData = useCallback((data) => {
    validateData(data, null);
  }, [validateData]);

  const handleInputChange = useCallback((index, fieldIndex, value) => {
    if (!inputRefs.current[index] || !inputRefs.current[index][fieldIndex]) {
      console.warn('Ref not initialized for:', index, fieldIndex);
      return; // Early exit if ref is not available
    }
    const inputRef = inputRefs.current[index][fieldIndex].current;
    const cursorPosition = inputRef?.selectionStart || null;
    const field = columns[fieldIndex].field;

    setEditableData((prevData) => {
      const currentValue = prevData[index][field];
      const isComplexField = field === "Country" || field === "State" || field === "POCUserID";

      if (currentValue === value && !isComplexField) return prevData;

      const newRow = { ...prevData[index] };

      // Normalize date fields when user enters them
      if ((field === 'TargetDT' || field === 'SinceDT' || field === 'NextTalkDT' || field === 'DueDT' || field === 'CompleteOnDT' || field === 'ExpectedByDT') && value) {
        newRow[field] = normalizeDateFormat(value);
      } else {
        newRow[field] = value || "";
      }

      if (field === "Country") {
        const country = memoizedCountries.find(c =>
          c.Country.toLowerCase().trim() === (value || "").toLowerCase().trim()
        );
        newRow.CountryID = country ? country.CountryID : 1;
        newRow.CC = country ? String(country.CC) : "91";
        newRow.State = "";
        newRow.StateID = 0;
      } else if (field === "State" && newRow.CountryID === 1) {
        const state = memoizedStates.find(s => s.State === value && s.CountryID === 1);
        newRow.StateID = state ? state.StateID : 0;
      } else if (field === "POCUserID") {
        const user = users.find(u => u.name?.toLowerCase().trim() === value?.toLowerCase().trim());
        newRow.POCUserID = user ? String(user.userID) : "";
        newRow.POCUserName = user ? user.name : "";
      } else if (field === "Product") {
        newRow.Model = "";
      }

      const updatedData = [...prevData];
      updatedData[index] = newRow;

      // Run live validation immediately so borders update on every keystroke
      validateData(updatedData, { rowIndex: index, fieldIndex });

      return updatedData;
    });

    if (cursorPosition !== null && (inputRef?.type === "text" || inputRef?.tagName === "TEXTAREA")) {
      requestAnimationFrame(() => {
        if (inputRef && inputRef.selectionStart !== undefined) {
          inputRef.setSelectionRange(cursorPosition, cursorPosition);
        }
      });
    }
  }, [columns, memoizedCountries, memoizedStates, users, validateData]);

  const handleInputBlur = useCallback((index, fieldIndex) => {
    // Use a longer timeout to ensure state has updated
    setTimeout(() => {
      // Get the current state and validate with it
      setEditableData(currentData => {
        validateData(currentData, { rowIndex: index, fieldIndex });
        return currentData;
      });
    }, 100); // Increased timeout to ensure state updates have completed
  }, [validateData]);

  // Gets sorted array of error cells for navigation
  const getSortedErrorCells = useCallback(() => {
    return Array.from(errorCells)
      .map(cellKey => {
        const [rowIndex, fieldIndex] = cellKey.split(',').map(Number);
        return { rowIndex, fieldIndex, cellKey };
      })
      .sort((a, b) => {
        if (a.rowIndex !== b.rowIndex) {
          return a.rowIndex - b.rowIndex;
        }
        return a.fieldIndex - b.fieldIndex;
      });
  }, [errorCells]);

  // Navigation helper functions for error cells
  const findNextErrorAfterPosition = useCallback((currentRowIndex, currentFieldIndex) => {
    const sortedErrors = getSortedErrorCells();
    if (sortedErrors.length === 0) return null;

    const nextError = sortedErrors.find(error => {
      if (error.rowIndex > currentRowIndex) return true;
      if (error.rowIndex === currentRowIndex && error.fieldIndex > currentFieldIndex) return true;
      return false;
    });

    return nextError || sortedErrors[0];
  }, [getSortedErrorCells]);

  const findPreviousErrorBeforePosition = useCallback((currentRowIndex, currentFieldIndex) => {
    const sortedErrors = getSortedErrorCells();
    if (sortedErrors.length === 0) return null;

    let previousError = null;
    for (let i = sortedErrors.length - 1; i >= 0; i--) {
      const error = sortedErrors[i];
      if (error.rowIndex < currentRowIndex) {
        previousError = error;
        break;
      }
      if (error.rowIndex === currentRowIndex && error.fieldIndex < currentFieldIndex) {
        previousError = error;
        break;
      }
    }

    return previousError || sortedErrors[sortedErrors.length - 1];
  }, [getSortedErrorCells]);

  // Finds currently active element in the table for focus management
  // Finds currently active element in the table for focus management
  const findActiveElement = useCallback((activeElement) => {
    if (!activeElement) return null;

    // Check directly for cell ID pattern: cell-${rowIndex}-${fieldIndex}
    const cellElement = activeElement.id?.startsWith('cell-') ? activeElement : activeElement.closest('[id^="cell-"]');
    if (cellElement?.id) {
      const match = cellElement.id.match(/^cell-(\d+)-(\d+)$/);
      if (match) {
        return { rowIndex: parseInt(match[1]), fieldIndex: parseInt(match[2]) };
      }
    }

    // Check date container pattern: date-${rowIndex}-${fieldIndex}
    const dateElement = activeElement.id?.startsWith('date-') ? activeElement : activeElement.closest('[id^="date-"]');
    if (dateElement?.id) {
      const match = dateElement.id.match(/date-(\d+)-(\d+)/);
      if (match) {
        return { rowIndex: parseInt(match[1]), fieldIndex: parseInt(match[2]) };
      }
    }

    // Check mobile container pattern: mobile-${rowIndex}-${fieldIndex}
    const mobileElement = activeElement.id?.startsWith('mobile-') ? activeElement : activeElement.closest('[id^="mobile-"]');
    if (mobileElement?.id) {
      const match = mobileElement.id.match(/mobile-(\d+)-(\d+)/);
      if (match) {
        return { rowIndex: parseInt(match[1]), fieldIndex: parseInt(match[2]) };
      }
    }

    // Check inputRefs
    if (inputRefs.current.length) {
      for (let rowIndex = 0; rowIndex < inputRefs.current.length; rowIndex++) {
        const row = inputRefs.current[rowIndex];
        if (!row) continue;
        for (let fieldIndex = 0; fieldIndex < row.length; fieldIndex++) {
          const ref = row[fieldIndex];
          if (ref?.current === activeElement || ref?.current?.contains?.(activeElement)) {
            return { rowIndex, fieldIndex };
          }
        }
      }
    }

    // Walk up to TD
    let element = activeElement;
    while (element && element.tagName !== 'TD') {
      element = element.parentElement;
      if (!element || element.tagName === 'BODY') break;
    }

    if (element?.tagName === 'TD') {
      if (element.id) {
        const match = element.id.match(/^cell-(\d+)-(\d+)$/);
        if (match) {
          return { rowIndex: parseInt(match[1]), fieldIndex: parseInt(match[2]) };
        }
      }

      const row = element.closest('tr[id^="row-"]');
      if (row?.id) {
        const rowMatch = row.id.match(/^row-(\d+)$/);
        if (rowMatch) {
          const rowIndex = parseInt(rowMatch[1]);
          const cells = Array.from(row.children);
          const fieldIndex = cells.indexOf(element);
          if (fieldIndex !== -1) {
            return { rowIndex, fieldIndex };
          }
        }
      }
    }

    return null;
  }, []);

  // Focuses on a specific cell programmatically
  const focusCell = useCallback((rowIndex, fieldIndex) => {
    let targetRef = inputRefs.current[rowIndex]?.[fieldIndex]?.current;

    // Robust fallback to DOM elements if ref is missing/detached (e.g. MobileInput, DatePicker, Selects)
    if (!targetRef) {
      const cell = document.getElementById(`cell-${rowIndex}-${fieldIndex}`);
      if (cell) {
        targetRef = cell.querySelector('input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), button[role="combobox"], button:not([disabled])');
      }
    }

    if (!targetRef) {
      const mobileContainer = document.querySelector(`#mobile-${rowIndex}-${fieldIndex}`);
      if (mobileContainer) {
        targetRef = mobileContainer.querySelector('input[type="tel"]') || mobileContainer.querySelector('input');
      }
    }

    if (!targetRef) {
      const dateContainer = document.querySelector(`#date-${rowIndex}-${fieldIndex}`);
      if (dateContainer) {
        targetRef = dateContainer.querySelector('button');
      }
    }

    if (targetRef) {
      const selectTrigger = targetRef.querySelector?.('[role="combobox"]') ||
        targetRef.querySelector?.('button[role="combobox"]') ||
        targetRef.querySelector?.('button');

      if (selectTrigger) {
        targetRef = selectTrigger;
      }
    }

    if (targetRef) {
      setPreviousCell({
        rowIndex: currentFocusedCell.rowIndex,
        fieldIndex: currentFocusedCell.fieldIndex
      });

      setCurrentFocusedCell({ rowIndex, fieldIndex });

      const doFocus = () => {
        try {
          if (targetRef && typeof targetRef.focus === 'function') {
            targetRef.focus({ preventScroll: true });
          }
        } catch (error) {
          console.error('Focus error:', error);
        }
      };

      doFocus();
      requestAnimationFrame(() => {
        if (document.activeElement !== targetRef) {
          doFocus();
        }
      });

      return true;
    }

    return false;
  }, [currentFocusedCell]);

  // Scrolls to error cell
  const scrollToError = useCallback((rowIndex, fieldIndex) => {
    if (fieldIndex === -1) return;

    // Find the cell or input DOM element
    const cell = document.getElementById(`cell-${rowIndex}-${fieldIndex}`);
    let target = cell ||
      document.getElementById(`date-${rowIndex}-${fieldIndex}`) ||
      document.getElementById(`mobile-${rowIndex}-${fieldIndex}`) ||
      inputRefs.current[rowIndex]?.[fieldIndex]?.current;

    if (!target) return;

    // Use 'center' horizontally and 'nearest' vertically to guarantee smooth left/right jump
    target.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center"
    });
  }, []);

  // Navigates to next error in the table
  const handleNextError = useCallback(() => {
    const sortedErrors = getSortedErrorCells();
    if (sortedErrors.length === 0) return;

    let nextError;
    const referenceRow = currentFocusedCell.rowIndex !== -1 ? currentFocusedCell.rowIndex : currentError.rowIndex;
    const referenceField = currentFocusedCell.fieldIndex !== -1 ? currentFocusedCell.fieldIndex : currentError.fieldIndex;

    if (referenceRow === -1 || referenceField === -1) {
      nextError = sortedErrors[0];
    } else {
      nextError = findNextErrorAfterPosition(referenceRow, referenceField);
    }

    if (nextError) {
      setCurrentError({ rowIndex: nextError.rowIndex, fieldIndex: nextError.fieldIndex });
      scrollToError(nextError.rowIndex, nextError.fieldIndex);
      setCurrentFocusedCell({
        rowIndex: nextError.rowIndex,
        fieldIndex: nextError.fieldIndex
      });
      focusCell(nextError.rowIndex, nextError.fieldIndex);
    }
  }, [currentFocusedCell, currentError, getSortedErrorCells, findNextErrorAfterPosition, scrollToError, focusCell]);

  // Navigates to previous error in the table
  const handlePreviousError = useCallback(() => {
    const sortedErrors = getSortedErrorCells();
    if (sortedErrors.length === 0) return;

    let previousError;
    const referenceRow = currentFocusedCell.rowIndex !== -1 ? currentFocusedCell.rowIndex : currentError.rowIndex;
    const referenceField = currentFocusedCell.fieldIndex !== -1 ? currentFocusedCell.fieldIndex : currentError.fieldIndex;

    if (referenceRow === -1 || referenceField === -1) {
      previousError = sortedErrors[sortedErrors.length - 1];
    } else {
      previousError = findPreviousErrorBeforePosition(referenceRow, referenceField);
    }

    if (previousError) {
      setCurrentError({ rowIndex: previousError.rowIndex, fieldIndex: previousError.fieldIndex });
      scrollToError(previousError.rowIndex, previousError.fieldIndex);
      setCurrentFocusedCell({
        rowIndex: previousError.rowIndex,
        fieldIndex: previousError.fieldIndex
      });
      focusCell(previousError.rowIndex, previousError.fieldIndex);
    }
  }, [currentFocusedCell, currentError, getSortedErrorCells, findPreviousErrorBeforePosition, scrollToError, focusCell]);

  // Gets rows without any validation errors
  const getErrorFreeRows = useCallback(() => {
    const errorFreeRows = [];
    const errorRowIndices = new Set();

    errorCells.forEach(cellKey => {
      const [rowIndex] = cellKey.split(',').map(Number);
      errorRowIndices.add(rowIndex);
    });

    editableData.forEach((row, index) => {
      if (!errorRowIndices.has(index)) {
        errorFreeRows.push({
          ...row,
          originalIndex: index
        });
      }
    });

    return errorFreeRows;
  }, [editableData, errorCells]);

  const errorFreeRows = useMemo(() => getErrorFreeRows(), [getErrorFreeRows]);

  // Save function 
  const handleSave = useCallback(async () => {
    const dataToSave = ignoreErrors ? errorFreeRows : editableData;

    if (!ignoreErrors && errorCells.size > 0) {
      toast.error(`Please fix ${errorCells.size} error(s) before saving.`);
      return;
    }

    if (ignoreErrors && dataToSave.length === 0) {
      toast.error('No valid rows to save. All rows have errors.');
      return;
    }

    setAPIError(null);

    try {
      dispatch(setGlobalLoading(true));

      const typeID = location.state?.threadInfo?.typeID || 1;

      const cleanedDataToSave = validateAndClearInvalidModels(dataToSave);

      const threadParams = cleanedDataToSave.map((row) => {
        // Extract custom fields
        const customFieldsData = [];
        if (customFields && customFields.length > 0) {
          customFields.forEach(field => {
            const fieldKey = `CF_${field.FID}`;
            const fieldValue = row[fieldKey];
            if (fieldValue && fieldValue.trim()) {
              customFieldsData.push({
                FID: field.FID,
                V: fieldValue.trim().substring(0, 50)
              });
            }
          });
        }

        return {
          BizName: String(row.BizName || '').substring(0, 150),
          ContactName: String(row.ContactName || '').substring(0, 150),
          ...(typeID === 2 ? {} : { Designation: String(row.Designation || '').substring(0, 150) }),
          Contacts: '[]',
          Mobile: String(row.Mobile || '').toString().trim().substring(0, 15),
          CC: Number(parseInt(row.CC || (row.CountryID === 1 ? 91 : 1)) || 91),
          Email: String(row.Email || '').substring(0, 255),
          POCUserID: Number(parseInt(row.POCUserID) || 0),
          Source: String(row.Source || '').substring(0, 50),
          Notes: String(row.Notes || '').substring(0, 1000),
          City: String(row.City || '').substring(0, 50),
          StateID: Number(parseInt(row.StateID) || 0),
          State: String(row.State || '').substring(0, 50),
          CountryID: Number(parseInt(row.CountryID) || 1),
          Country: String(row.Country || '').substring(0, 50),
          TypeID: typeID,
          ConnID: 0,
          Type: typeID === 2 ? 'ORD' : 'INQ',
          MainProduct: String(row.Product || '').substring(0, 100),
          MainQty: String(row.Quantity || '').substring(0, 50),
          Items: '[]',
          DueDT: validatorObj.formatSmallDateTime(row.DueDT || row.TargetDT || new Date()),
          CompleteOnDT: typeID === 2 ? validatorObj.formatSmallDateTime(row.CompleteOnDT) : null,
          ExpectedByDT: typeID !== 2 ? validatorObj.formatSmallDateTime(row.ExpectedByDT) : null,
          CurrStage: typeID === 2 ? 'New Sale' : 'New',
          ExtraFields: JSON.stringify(customFieldsData),
          Model: String(row.Model || '').substring(0, 100),
          BizProspect: (parseFloat(row.BizProspect) || 0.0),
          Reference: String(row.Reference || '').substring(0, 20),
          SinceDT: validatorObj.formatSmallDateTime(row.SinceDT || new Date()),
          Stage: 1,
          SrcActionID: '',
          NextTask: String(row.NextTalkNotes || '').substring(0, 4000),
          NextTaskDT: validatorObj.formatSmallDateTime(row.NextTalkDT),
          NextTaskType: row.NextTalkNotes ? (String(row.NextTaskType || '').replace(/^-$/, '') || 'Internal') : '',
        };
      });

      const cleanedParams = threadParams.map(param => {
        const cleaned = {};
        Object.keys(param).forEach(key => {
          const value = param[key];
          if (value !== undefined && value !== null && value !== 'null') {
            cleaned[key] = value;
          }
        });
        return cleaned;
      });

      const result = await saveInquiriesBulk(cleanedParams, 'EXCEL', typeID);

      if (!result.success) {
        throw new Error(result.message || 'Inquiry import failed');
      }

      setSaveDialog(false);

      const threadTypeName = (location.state?.threadInfo?.typeID || location.state?.threadType?.typeID) === 2 ? 'orders' : 'inquiries';
      const successMessage = `${dataToSave.length}/${editableData.length} ${threadTypeName} saved successfully.` +
        (ignoreErrors && dataToSave.length < editableData.length ? ` (${editableData.length - dataToSave.length} rows with errors were skipped)` : '');

      toast.success(successMessage);

      setTimeout(() => {
        navigate('/threads');
      }, 3000);

    } catch (err) {
      setAPIError(err.message);
    } finally {
      dispatch(setGlobalLoading(false));
    }
  }, [ignoreErrors, errorFreeRows, editableData, errorCells.size, dispatch, navigate, location.state, customFields, validateAndClearInvalidModels]);

  // Keyboard navigation handler for tab traversal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "Tab") return;

      const activeElement = document.activeElement;
      const currentCell = findActiveElement(activeElement);

      if (currentCell) {
        e.preventDefault();

        let nextCell = getNextCell(
          currentCell.rowIndex,
          currentCell.fieldIndex,
          e.shiftKey ? "backward" : "forward"
        );

        if (nextCell) {
          focusCell(nextCell.rowIndex, nextCell.fieldIndex);
        }
      } else if (inputRefs.current.length > 0) {
        e.preventDefault();
        focusCell(0, 1);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [findActiveElement, getNextCell, focusCell]);

  // Focus tracking for current cell management
  useEffect(() => {
    const handleFocusIn = (e) => {
      const activeElement = e.target;
      const currentCell = findActiveElement(activeElement);

      if (currentCell) {
        setPreviousCell(prev => ({
          rowIndex: prev.rowIndex,
          fieldIndex: prev.fieldIndex
        }));

        setCurrentFocusedCell({
          rowIndex: currentCell.rowIndex,
          fieldIndex: currentCell.fieldIndex
        });

        const cellKey = getCellKey(currentCell.rowIndex, currentCell.fieldIndex);
        const hasError = errorCells.has(cellKey);
        setCurrentError(hasError ? { rowIndex: currentCell.rowIndex, fieldIndex: currentCell.fieldIndex } : { rowIndex: -1, fieldIndex: -1 });
      }
    };

    document.addEventListener("focusin", handleFocusIn);
    return () => document.removeEventListener("focusin", handleFocusIn);
  }, [findActiveElement, errorCells, getCellKey]);

  // Button state helpers for error navigation
  const getErrorNavigationButtonStates = useCallback(() => {
    const sortedErrors = getSortedErrorCells();
    if (sortedErrors.length === 0) return { nextDisabled: true, prevDisabled: true };

    const referenceRow = currentFocusedCell.rowIndex !== -1 ? currentFocusedCell.rowIndex : currentError.rowIndex;
    const referenceField = currentFocusedCell.fieldIndex !== -1 ? currentFocusedCell.fieldIndex : currentError.fieldIndex;

    if (referenceRow === -1 || referenceField === -1) return { nextDisabled: false, prevDisabled: false };

    const nextError = findNextErrorAfterPosition(referenceRow, referenceField);
    const previousError = findPreviousErrorBeforePosition(referenceRow, referenceField);

    const nextDisabled = nextError === sortedErrors[0] && (
      referenceRow > sortedErrors[sortedErrors.length - 1].rowIndex ||
      (referenceRow === sortedErrors[sortedErrors.length - 1].rowIndex &&
        referenceField >= sortedErrors[sortedErrors.length - 1].fieldIndex)
    );

    const currentErrorIndex = sortedErrors.findIndex(error =>
      error.rowIndex === referenceRow && error.fieldIndex === referenceField
    );
    const prevDisabled = currentErrorIndex === 0 || (currentErrorIndex === -1 &&
      (referenceRow < sortedErrors[0].rowIndex ||
        (referenceRow === sortedErrors[0].rowIndex && referenceField <= sortedErrors[0].fieldIndex)));

    return { nextDisabled, prevDisabled };
  }, [getSortedErrorCells, currentFocusedCell, currentError, findNextErrorAfterPosition, findPreviousErrorBeforePosition]);

  const { nextDisabled, prevDisabled } = getErrorNavigationButtonStates();

  const triggerValidation = useCallback((rowIndex, fieldIndex) => {
    // Use requestAnimationFrame to ensure all state updates are complete
    requestAnimationFrame(() => {
      setEditableData(currentData => {
        validateData(currentData, { rowIndex, fieldIndex });
        return currentData;
      });
    });
  }, [validateData]);

  // Renders appropriate input field based on column type
  const renderInputField = useCallback((column, row, rowIndex, fieldIndex, isCurrentErrorCell = false, rowHasError = false) => {
    const value = column.field === "No"
      ? (rowIndex + 1).toString()
      : column.field === "POCUserID"
        ? row.POCUserName || ""
        : row[column.field] || "";

    const cellKey = getCellKey(rowIndex, fieldIndex);
    const hasError = errorCells.has(cellKey);
    const baseClassName = cn(
      threadImpClasses.baseInput,
      isCurrentErrorCell
        ? threadImpClasses.activeErrorInput
        : hasError
          ? threadImpClasses.errorInput
          : threadImpClasses.normalInput
    );

    // Ensure refs are initialized
    if (!inputRefs.current[rowIndex]) {
      inputRefs.current[rowIndex] = [];
    }
    if (!inputRefs.current[rowIndex][fieldIndex]) {
      inputRefs.current[rowIndex][fieldIndex] = React.createRef();
    }

    const commonProps = {
      ref: inputRefs.current[rowIndex][fieldIndex],
      value,
      onChange: (e) => handleInputChange(rowIndex, fieldIndex, e.target.value),
      className: baseClassName,
    };

    const getSelectOptions = () => {
      if (column.field === "Country") return memoizedCountries.map(c => c.Country);
      if (column.field === "State" && row.CountryID === 1)
        return memoizedStates.filter(s => s.CountryID === 1).map(s => s.State);
      if (column.field === "POCUserID") return users.map(u => u.name);
      if (column.field === "Product") return products.map(p => p.name || p.PNm);
      if (column.field === "NextTaskType") return taskTypes.map(t => t.Typ);
      return [];
    };

    const fieldRenderers = {
      select: () => {
        // Ensure ref is initialized for select
        if (!inputRefs.current[rowIndex]) {
          inputRefs.current[rowIndex] = [];
        }
        if (!inputRefs.current[rowIndex][fieldIndex]) {
          inputRefs.current[rowIndex][fieldIndex] = React.createRef();
        }
        return (
          <Select
            value={value || "__none__"}
            onValueChange={(val) => {
              handleInputChange(rowIndex, fieldIndex, val === "__none__" ? "" : val);
              setTimeout(() => handleInputBlur(rowIndex, fieldIndex), 0);
            }}
          >
            <SelectTrigger
              ref={inputRefs.current[rowIndex][fieldIndex]}
              className={cn("h-10 text-sm", baseClassName)}
              tabIndex={0}
            >
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="__none__">Select</SelectItem>
                {getSelectOptions().map((option, i) => (
                  <SelectItem key={i} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        );
      },

      textarea: () => (
        <Textarea
          {...commonProps}
          className={`${baseClassName} resize-none min-h-[40px]`}
          rows={2}
          onBlur={() => handleInputBlur(rowIndex, fieldIndex)}
        />
      ),

      date: () => {
        let dateValue = "";
        if (value) {
          const d = new Date(value);
          if (!isNaN(d.getTime())) {
            try {
              // FIX: Use local date without timezone conversion
              const year = d.getFullYear();
              const month = String(d.getMonth() + 1).padStart(2, '0');
              const day = String(d.getDate()).padStart(2, '0');
              const hours = String(d.getHours()).padStart(2, '0');
              const minutes = String(d.getMinutes()).padStart(2, '0');
              const seconds = String(d.getSeconds()).padStart(2, '0');
              dateValue = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
            } catch {
              dateValue = "";
            }
          }
        }

        const today = new Date();

        // Calculate minimum date for date-range fields based on Lead Since
        const sinceDateForMinDate = row.SinceDT?.trim()
          ? new Date(row.SinceDT)
          : today;

        const dateConstraints = {
          SinceDT: allowFutureDT ? {} : { maxDate: today },
          NextTalkDT: { minDate: today },
          TargetDT: { minDate: today },
          DueDT: { minDate: sinceDateForMinDate },
          CompleteOnDT: { minDate: sinceDateForMinDate },
          ExpectedByDT: { minDate: sinceDateForMinDate }
        };

        const datePickerAdditionalCss = isCurrentErrorCell
          ? "!border-2 !border-red-500 !bg-red-50/70 hover:!border-red-500 focus:!border-red-500 !shadow-none !ring-0 focus:!ring-0"
          : hasError
            ? "!border !border-red-300 !bg-red-50/40 hover:!border-red-400 focus:!border-red-400 !shadow-none !ring-0 focus:!ring-0"
            : "border-gray-200 hover:border-gray-300 focus:border-gray-400";

        return (
          <div
            id={`date-${rowIndex}-${fieldIndex}`}
            className={cn(
              "w-full [&_button]:!h-10 [&_div.space-y-1\\.5]:space-y-0",
              isCurrentErrorCell
                ? "[&_button]:!border-2 [&_button]:!border-red-500 [&_button]:!bg-red-50/70 [&_button]:hover:!border-red-500 [&_button]:focus:!border-red-500 [&_span]:!bg-transparent"
                : hasError
                  ? "[&_button]:!border [&_button]:!border-red-300 [&_button]:!bg-red-50/40 [&_button]:hover:!border-red-400 [&_button]:focus:!border-red-400 [&_span]:!bg-transparent"
                  : "[&_button]:border-gray-200 [&_button]:hover:border-gray-300 [&_button]:focus:border-gray-400"
            )}
          >
            <DatePicker
              value={dateValue}
              onChange={(date) => {
                if (date) {
                  const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
                  const dateVal = localDate.toISOString().slice(0, 19);
                  handleInputChange(rowIndex, fieldIndex, dateVal);
                } else {
                  handleInputChange(rowIndex, fieldIndex, "");
                }
                setEditableData(currentData => {
                  validateData(currentData, { rowIndex, fieldIndex });
                  return currentData;
                });
              }}
              {...(dateConstraints[column.field] || {})}
              placeholder="Select Date"
              fieldID={`date-${rowIndex}-${fieldIndex}`}
              id={`date-${rowIndex}-${fieldIndex}`}
              ref={commonProps.ref}
              additionalCss={datePickerAdditionalCss}
            />
          </div>
        );
      },

      email: () => (
        <Input
          {...commonProps}
          type="email"
          placeholder="email@example.com"
          onChange={(e) => {
            commonProps.onChange(e);
            setEditableData(currentData => {
              validateData(currentData, { rowIndex, fieldIndex });
              return currentData;
            });
          }}
          onBlur={() => handleInputBlur(rowIndex, fieldIndex)}
        />
      ),
      mobile: () => (
        <div
          id={`mobile-${rowIndex}-${fieldIndex}`}
          className={cn(
            "w-full [&_div.flex]:!h-10 [&_button]:!h-10 [&_input]:!h-10 [&_div.space-y-1\\.5]:space-y-0",
            "[&_input]:!border-none [&_input]:!shadow-none [&_input]:!ring-0 [&_input]:focus:!ring-0 [&_button]:!shadow-none",
            isCurrentErrorCell
              ? "[&_div.sahi-input]:!border-2 [&_div.sahi-input]:!border-red-500 [&_div.sahi-input]:!bg-red-50/70 [&_div.sahi-input]:hover:!border-red-500 [&_div.sahi-input]:focus-within:!border-red-500 [&_div.sahi-input]:!ring-0"
              : hasError
                ? "[&_div.sahi-input]:!border [&_div.sahi-input]:!border-red-300 [&_div.sahi-input]:!bg-red-50/40 [&_div.sahi-input]:hover:!border-red-400 [&_div.sahi-input]:focus-within:!border-red-400 [&_div.sahi-input]:!ring-0"
                : "[&_div.sahi-input]:!border-gray-200 [&_div.sahi-input]:hover:!border-gray-300 [&_div.sahi-input]:focus-within:!border-gray-400 [&_div.sahi-input]:!ring-0"
          )}
        >
          <MobileInput
            fieldID={`mobile-${rowIndex}-${fieldIndex}`}
            label=""
            placeholder="Enter mobile number"
            value={value}
            onChange={(e) => {
              const rawValue = e.target.value;

              // If pasted value starts with '+' (e.g., +918908908902)
              if (rawValue && rawValue.startsWith('+')) {
                const cleanDigits = rawValue.replace(/\D/g, '');
                const matchedCountry = [...memoizedCountries]
                  .sort((a, b) => String(b.CC).length - String(a.CC).length)
                  .find(c => cleanDigits.startsWith(String(c.CC)));

                if (matchedCountry) {
                  const mobileOnly = cleanDigits.substring(String(matchedCountry.CC).length);
                  setEditableData(prevData => {
                    const newData = [...prevData];
                    newData[rowIndex] = {
                      ...newData[rowIndex],
                      CountryID: matchedCountry.CountryID,
                      CC: String(matchedCountry.CC),
                      Country: matchedCountry.Country,
                      Mobile: mobileOnly,
                      State: "",
                      StateID: 0
                    };
                    validateData(newData, { rowIndex, fieldIndex });
                    return newData;
                  });
                  return;
                }
              }

              if (e.target.name === 'mobile-' + rowIndex + '-' + fieldIndex || e.target.name === 'mobile') {
                handleInputChange(rowIndex, fieldIndex, rawValue);
                setEditableData(currentData => {
                  validateData(currentData, { rowIndex, fieldIndex });
                  return currentData;
                });
              } else if (e.target.name === 'country') {
                const countryFieldIndex = columns.findIndex(col => col.field === 'Country');
                if (countryFieldIndex !== -1) {
                  handleInputChange(rowIndex, countryFieldIndex, e.target.value);
                }
              } else if (e.target.name === 'state') {
                const stateFieldIndex = columns.findIndex(col => col.field === 'State');
                if (stateFieldIndex !== -1) {
                  handleInputChange(rowIndex, stateFieldIndex, e.target.value);
                }
              }
            }}
            onBlur={() => handleInputBlur(rowIndex, fieldIndex)}
            selectedCountryCode={String(row.CC || "91")}
            onCountryCodeChange={(countryID) => {
              const country = memoizedCountries.find(c => String(c.CC) === String(countryID));
              if (country) {
                setEditableData(prevData => {
                  const newData = [...prevData];
                  newData[rowIndex] = {
                    ...newData[rowIndex],
                    CountryID: country.CountryID,
                    CC: String(country.CC),
                    Country: country.Country,
                    State: "",
                    StateID: 0
                  };
                  validateData(newData, { rowIndex, fieldIndex });
                  const countryColIdx = columns.findIndex(c => c.field === 'Country');
                  if (countryColIdx !== -1) {
                    validateData(newData, { rowIndex, fieldIndex: countryColIdx });
                  }
                  return newData;
                });
              }
            }}
            className="w-full !space-y-0"
            ref={inputRefs.current[rowIndex][fieldIndex]}
          />
        </div>
      ),
      dynamic: () => {
        if (column.field === "State" && row.CountryID === 1) {
          return (
            <Select
              value={value}
              onValueChange={(val) => handleInputChange(rowIndex, fieldIndex, val)}
            >
              <SelectTrigger
                ref={inputRefs.current[rowIndex][fieldIndex]}
                className={cn("h-10 text-sm", baseClassName)}
                tabIndex={0}
              >
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {states
                  .filter(s => s.CountryID === 1)
                  .map((s, i) => (
                    <SelectItem key={i} value={s.State}>
                      {s.State}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          );
        }
        return (
          <Input
            {...commonProps}
            type="text"
            onBlur={() => handleInputBlur(rowIndex, fieldIndex)}
          />
        );
      },

      text: () => {
        if (column.field === "No") {
          return (
            <div className="flex items-center justify-center h-10">
              <span className={cn(
                "text-sm font-medium",
                rowHasError ? "text-red-500" : "text-gray-600"
              )}>
                {value}
              </span>
            </div>
          );
        }

        // Handle custom fields
        if (column.customField) {
          return (
            <Input
              {...commonProps}
              type="text"
              placeholder={`Enter ${column.display}`}
              maxLength={50}
              onBlur={() => handleInputBlur(rowIndex, fieldIndex)}
            />
          );
        }

        return (
          <Input
            {...commonProps}
            type="text"
            onBlur={() => handleInputBlur(rowIndex, fieldIndex)}
          />
        );
      },

      number: () => (
        <div
          className={cn(
            "w-full [&_div.flex]:!h-10 [&_input]:!h-10 [&_div.space-y-1\\.5]:space-y-0",
            "[&_input]:!border-none [&_input]:!shadow-none [&_input]:!ring-0 [&_input]:focus:!ring-0",
            isCurrentErrorCell
              ? "[&_div.sahi-input]:!border-2 [&_div.sahi-input]:!border-red-500 [&_div.sahi-input]:!bg-red-50/70 [&_div.sahi-input]:hover:!border-red-500 [&_div.sahi-input]:focus-within:!border-red-500 [&_div.sahi-input]:!ring-0"
              : hasError
                ? "[&_div.sahi-input]:!border [&_div.sahi-input]:!border-red-300 [&_div.sahi-input]:!bg-red-50/40 [&_div.sahi-input]:hover:!border-red-400 [&_div.sahi-input]:focus-within:!border-red-400 [&_div.sahi-input]:!ring-0"
                : "[&_div.sahi-input]:!border-gray-200 [&_div.sahi-input]:hover:!border-gray-300 [&_div.sahi-input]:focus-within:!border-gray-400 [&_div.sahi-input]:!ring-0"
          )}
        >
          <BusinessInput
            fieldID={`amount-${rowIndex}-${fieldIndex}`}
            value={value}
            onChange={(e) => {
              handleInputChange(rowIndex, fieldIndex, e.target.value);
              setEditableData(currentData => {
                validateData(currentData, { rowIndex, fieldIndex });
                return currentData;
              });
            }}
            onBlur={() => handleInputBlur(rowIndex, fieldIndex)}
            className="w-full !space-y-0"
            ref={inputRefs.current[rowIndex][fieldIndex]}
            maxIntegerDigits={10}
            maxDecimalPlaces={2}
            placeholder="0.00"
          />
        </div>
      ),

      productCombobox: () => {
        // Ensure ref is initialized for product combobox
        if (!inputRefs.current[rowIndex]) {
          inputRefs.current[rowIndex] = [];
        }
        if (!inputRefs.current[rowIndex][fieldIndex]) {
          inputRefs.current[rowIndex][fieldIndex] = React.createRef();
        }
        return (
          <Select
            value={value || "__none__"}
            onValueChange={(val) => {
              handleInputChange(rowIndex, fieldIndex, val === "__none__" ? "" : val);
              setTimeout(() => handleInputBlur(rowIndex, fieldIndex), 0);
            }}
          >
            <SelectTrigger
              ref={inputRefs.current[rowIndex][fieldIndex]}
              className={cn("h-10 text-sm", baseClassName)}
              tabIndex={0}
            >
              <SelectValue placeholder="Select Offering" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="__none__">Select Offering</SelectItem>
                {products.map((product, i) => (
                  <SelectItem key={i} value={product.name || product.PNm}>
                    {product.name || product.PNm}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        );
      },

      modelCombobox: () => {
        const selectedProduct = row.Product?.trim()
          ? products.find(p => (p.name || p.PNm || '').toLowerCase().trim() === row.Product.toLowerCase().trim())
          : null;

        if (!selectedProduct) {
          return (
            <Input
              {...commonProps}
              type="text"
              placeholder="Select product first"
              disabled={true}
              onBlur={() => handleInputBlur(rowIndex, fieldIndex)}
            />
          );
        }

        let modelOptions = [];
        try {
          const modelsData = selectedProduct.Mdls || selectedProduct.mdls || selectedProduct.Models || selectedProduct.models;
          if (modelsData) {
            const models = typeof modelsData === 'string' ? JSON.parse(modelsData) : modelsData;
            if (Array.isArray(models) && models.length > 0) {
              modelOptions = models
                .map(m => {
                  const modelName = m.MNm || m.mNm || m.name || '';
                  return modelName ? { value: modelName, label: modelName } : null;
                })
                .filter(m => m !== null && m.value);
            }
          }
        } catch (e) {
          console.error('Error parsing models:', e);
        }

        const offeringType = selectedProduct.PTID || selectedProduct.ptid || selectedProduct.offeringType || 1;
        const fieldLabel = offeringType === 1 ? 'Model' : 'Package';

        if (!inputRefs.current[rowIndex]) {
          inputRefs.current[rowIndex] = [];
        }
        if (!inputRefs.current[rowIndex][fieldIndex]) {
          inputRefs.current[rowIndex][fieldIndex] = React.createRef();
        }

        return (
          <Select
            value={value || "__none__"}
            onValueChange={(val) => {
              handleInputChange(rowIndex, fieldIndex, val === "__none__" ? "" : val);
              setTimeout(() => handleInputBlur(rowIndex, fieldIndex), 0);
            }}
          >
            <SelectTrigger
              ref={inputRefs.current[rowIndex][fieldIndex]}
              className={cn("h-10 text-sm", baseClassName)}
              tabIndex={0}
            >
              <SelectValue placeholder={`Select ${fieldLabel}`} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="__none__">Select {fieldLabel}</SelectItem>
                {modelOptions.map((option, i) => (
                  <SelectItem key={i} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        );
      },
    };

    return fieldRenderers[column.type] ? fieldRenderers[column.type]() : fieldRenderers.text();
  }, [errorCells, handleInputChange, getCellKey, memoizedCountries, memoizedStates, users, products, taskTypes, handleInputBlur, allowFutureDT]);

  // Gets dialog content based on current state
  const getDialogContent = () => {
    const errorFreeRowsCount = errorFreeRows.length;
    const totalRows = editableData.length;
    const hasErrors = errorCells.size > 0;
    const typeID = location.state?.threadInfo?.typeID || location.state?.threadType?.typeID || 1;

    const singular = getThreadNameFromTypeID(typeID, threadTypes, "TypSng");
    const plural = getThreadNameFromTypeID(typeID, threadTypes, "Typ");

    if (ignoreErrors && hasErrors) {
      return {
        title: `Import Valid ${plural}`,
        message: `Found ${totalRows - errorFreeRowsCount} ${pluralize(totalRows - errorFreeRowsCount, "record", "records")} with errors that will be skipped. Are you sure you wish to import ${errorFreeRowsCount} valid ${pluralize(errorFreeRowsCount, singular, plural)}?`,
        confirmText: `Import ${errorFreeRowsCount} ${pluralize(errorFreeRowsCount, singular, plural)}`
      };
    }

    return {
      title: `${totalRows === 1 ? singular : plural} Import`,
      message: `Are you sure you want to import ${totalRows === 1 ? '' : 'all '}${totalRows} ${pluralize(totalRows, singular, plural)}?`,
      confirmText: `Import ${totalRows === 1 ? '' : 'All '}${totalRows} ${pluralize(totalRows, singular, plural)}`
    };
  };

  const dialogContent = getDialogContent();

  // Calculate pagination
  const ROWS_PER_PAGE = 50; // Display 50 rows per page
  const totalPages = editableData.length > 0 ? Math.ceil(editableData.length / ROWS_PER_PAGE) : 0;
  const displayedData = editableData.slice(
    currentDisplayPage * ROWS_PER_PAGE,
    (currentDisplayPage + 1) * ROWS_PER_PAGE
  );

  return (
    <div
      className="h-[95vh] flex flex-col space-y-3 lg:space-y-6 overflow-hidden scrollbar-hide"
      onLoad={() => {
        setTimeout(() => {
          const mainDiv = document.querySelector('div[class*="h-\\[95vh\\]"]');
        }, 100);
      }}
    >
      {/* Validation Progress Bar */}
      {/* {isValidating && (
        <div className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-800">Processing & Validating Data...</span>
            <span className="text-sm text-blue-600">{validationProgress}%</span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${validationProgress}%` }}
            ></div>
          </div>
        </div>
      )} */}

      {/* Header */}
      <div className="flex justify-between items-center gap-3">
        <div className="flex items-center flex-row justify-between flex-grow">
          {/* Left Side: Title */}
          <div className="flex flex-col mr-4">
            <h1 className="text-3xl font-bold text-text-color web-header-name">
              {(location.state?.threadInfo?.typeID || location.state?.threadType?.typeID) === 2 ? 'Sale' : 'Inquiry'} Import (Corrections)
            </h1>
          </div>

          {/* Center/Right Info Boxes */}
          <div className="flex items-center gap-3">
            {/* Bad Rows Box */}
            <div
              className={cn(
                "flex items-center px-3 lg:px-4 h-8 lg:h-10",
                "w-fit max-w-[240px] bg-white text-black rounded-lg border filter-border shadow-sm"
              )}
            >
              <span className="f-row gap-1 whitespace-nowrap text-xs lg:text-sm">
                <span>Bad Rows: </span>
                <span className={cn("font-medium", (editableData.length - errorFreeRows.length) > 0 ? "text-sahi-red" : "text-sahi-green")}>
                  {editableData.length - errorFreeRows.length}/{editableData.length}
                </span>
              </span>
            </div>

            {/* Total Errors Box */}
            <div
              className={cn(
                "flex items-center px-3 lg:px-4 h-8 lg:h-10",
                "w-fit max-w-[240px] bg-white text-black rounded-lg border filter-border shadow-sm"
              )}
            >
              <span className="f-row gap-1 whitespace-nowrap text-xs lg:text-sm">
                <span>Total Errors: </span>
                <span className={cn("font-medium", errorCells.size > 0 ? "text-sahi-red" : "text-sahi-green")}>
                  {errorCells.size}
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <TooltipProvider>
            {errorCells.size > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setIgnoreErrors(prev => !prev)}
                    disabled={errorCells.size === 0}
                    className="cursor-pointer"
                  >
                    <div className="f-row items-center justify-between gap-2 px-1">
                      <Checkbox
                        checked={ignoreErrors}
                        disabled={errorCells.size === 0}
                        className={threadImpClasses.checkbox}
                      />
                      <span className="font-medium text-sm lg:text-base text-black select-none">Ignore Errors</span>
                    </div>
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {errorCells.size > 0
                      ? `${errorFreeRows.length} out of ${editableData.length} rows are valid and can be imported`
                      : "No errors found"}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <Button
                    variant="secondary"
                    className="button-config text-base flex items-center py-[6px] px-[10px]"
                    onClick={handlePreviousError}
                    disabled={prevDisabled}
                  >
                    <ChevronUp className="h-4 w-4" />
                    Previous Error
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Go to Previous Error</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <Button
                    variant="secondary"
                    className="button-config text-base flex items-center py-[6px] px-[10px]"
                    onClick={handleNextError}
                    disabled={nextDisabled}
                  >
                    <ChevronDown className="h-4 w-4" />
                    Next Error
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Go to Next Error</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <Button
                    className="button-config text-base sahi-primary-button flex items-center py-[6px] px-[10px]"
                    onClick={() => setSaveDialog(true)}
                    disabled={
                      (!ignoreErrors && errorCells.size > 0) ||
                      errorFreeRows.length === 0
                    }
                  >
                    <Check className="h-4 w-4" />
                    Save
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {errorFreeRows.length === 0
                    ? "No valid rows to save"
                    : !ignoreErrors && errorCells.size > 0
                      ? "Fix all errors or enable 'Ignore Errors' to save valid rows"
                      : ignoreErrors && errorCells.size > 0
                        ? `Save ${errorFreeRows.length} valid rows`
                        : "Save all rows"}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Main Card */}
      <Card className={`${threadImpClasses.card} max-h-[calc(100vh-20px)]`}>
        <CardContent
          className="flex-1 flex flex-col overflow-hidden min-h-0"
        >
          {apiError && (
            <div className="bg-red-50 border border-red-200 text-sahi-red px-4 py-3 sahi-radius mb-4">
              {apiError}
            </div>
          )}

          {editableData.length === 0 && !isValidating && (
            <div className="text-gray-600 mb-4 text-center py-8">
              No data loaded. Please upload a valid Excel file.
            </div>
          )}

          {editableData.length > 0 && (
            <>
              <VirtualizedTable
                data={displayedData}
                columns={columns}
                errorCells={errorCells}
                cellErrors={cellErrors}
                currentError={currentError}
                getCellKey={getCellKey}
                renderInputField={renderInputField}
              />

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between gap-4 mt-4 px-4 py-3 bg-gray-50 border-t">
                  <div className="text-sm text-gray-600">
                    Showing <span className="font-semibold">{currentDisplayPage * ROWS_PER_PAGE + 1}</span> to{' '}
                    <span className="font-semibold">
                      {Math.min((currentDisplayPage + 1) * ROWS_PER_PAGE, editableData.length)}
                    </span>{' '}
                    of <span className="font-semibold">{editableData.length}</span> rows (Page {currentDisplayPage + 1} of {totalPages})
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setCurrentDisplayPage(Math.max(0, currentDisplayPage - 1))}
                      disabled={currentDisplayPage === 0}
                      className="px-3"
                    >
                      Previous
                    </Button>
                    <div className="flex gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageNum = currentDisplayPage < 3 ? i : currentDisplayPage - 2 + i;
                        if (pageNum >= totalPages) return null;
                        return (
                          <Button
                            key={pageNum}
                            variant={currentDisplayPage === pageNum ? "default" : "secondary"}
                            size="sm"
                            onClick={() => setCurrentDisplayPage(pageNum)}
                            className="px-3"
                          >
                            {pageNum + 1}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setCurrentDisplayPage(Math.min(totalPages - 1, currentDisplayPage + 1))}
                      disabled={currentDisplayPage === totalPages - 1}
                      className="px-3"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Save Confirmation Dialog */}
      {saveDialog && (
        <Dialog open={saveDialog} onOpenChange={setSaveDialog}>
          <DialogContent className="w-full max-w-6xl pb-4 sahi-radius overflow-hidden">
            <DialogHeader className="dialog-header flex items-center justify-between px-4 pt-4">
              <div className="flex items-center gap-2">
                <DialogTitle className="font-semibold text-lg">
                  {dialogContent.title}
                </DialogTitle>
              </div>
              <DialogClose className="p-1 sahi-radius">
                <X className="h-5 w-5" />
              </DialogClose>
            </DialogHeader>

            <div className="px-4">
              {dialogContent.message}
            </div>

            <div className="w-fit !sahi-radius px-4">
              <div className="f-row gap-3 justify-between">
                <Button onClick={handleSave} className="!w-full lg:w-fit">
                  <Check className="h-4 w-4" />
                  {dialogContent.confirmText}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setSaveDialog(false)}
                  className="!w-full lg:w-fit cls-btn"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
});

export default CorrectExcelThreads;